import { PaymentMethod } from '@parkingbase/ecommerce-api-demo';

import { create } from 'zustand';
import { PaymentMethodService } from '@/services/paymentMethod.service';
import { logger } from '@/lib/logger';
import { extractApiErrorMessage } from '@/lib/api-error-utils';

interface PaymentMethodState {
  paymentMethods: PaymentMethod[];
  isLoading: boolean;
  error: string | null;

  // Actions
  getAllPaymentMethods: () => Promise<void>;
  setPaymentMethods: (paymentMethods: PaymentMethod[]) => void;
  reset: () => void;
  createPaymentMethod: (
    paymentMethod: PaymentMethod,
    recaptchaToken?: string
  ) => Promise<PaymentMethod | null>;
  updatePaymentMethod: (
    id: string,
    paymentMethod: PaymentMethod,
    recaptchaToken?: string
  ) => Promise<PaymentMethod | null>;
  deletePaymentMethod: (id: string, recaptchaToken?: string) => Promise<void>;
  /** Mints a Stripe ACH SetupIntent for the bank-account confirm flow. */
  createStripeAchSetupIntent: (
    garageId: string
  ) => Promise<{ clientSecret: string; customerId: string }>;
  /** Reloads a SetupIntent to resume microdeposit verification (client secret + kind). */
  getStripeSetupIntent: (
    setupIntentId: string,
    garageId: string
  ) => Promise<{ clientSecret: string; status: string; microdepositKind: string }>;
  /** Flips PENDING → VERIFIED after microdeposits are confirmed; returns the new status. */
  refreshStripeAchStatus: (
    paymentMethodId: string,
    garageId: string
  ) => Promise<{ achVerificationStatus: string }>;
  /** Re-reads an Adyen bank's verification status (PENDING until Adyen's webhook, PB-2595). */
  refreshAdyenAchStatus: (
    paymentMethodId: string,
    garageId: string
  ) => Promise<{ achVerificationStatus?: string }>;
  /** Mints a PayPal Vault v3 SETUP token for the add-card flow (PB-3067). */
  createPaypalSetupToken: (garageId: string) => Promise<{ id: string }>;
  setDefaultPaymentMethod: (id: string, recaptchaToken?: string) => Promise<void>;
  getDefaultPaymentMethod: () => PaymentMethod | null;
  setError: (error: string) => void;
  clearError: () => void;
}

const paymentMethodService = new PaymentMethodService();

export const usePaymentMethodStore = create<PaymentMethodState>((set, get) => ({
  paymentMethods: [],
  isLoading: false,
  error: null,

  setPaymentMethods: (paymentMethods: PaymentMethod[]) => set({ paymentMethods }),

  reset: () => set({ paymentMethods: [], isLoading: false, error: null }),

  setError: (error: string) => set({ error }),
  clearError: () => set({ error: null }),

  getDefaultPaymentMethod: () => {
    const paymentMethods = get().paymentMethods;
    return (
      (paymentMethods || []).find((pm) => pm.defaultPaymentMethod) ||
      (paymentMethods?.length > 0 ? paymentMethods[0] : null)
    );
  },

  getAllPaymentMethods: async () => {
    if (get().isLoading) return;
    try {
      set({ isLoading: true, error: null });
      const paymentMethods = await paymentMethodService.getAllPaymentMethods();

      // Sort: Primary first, then by creation date (newest first)
      if (paymentMethods?.length > 0) {
        paymentMethods.sort((a, b) => {
          if (a.defaultPaymentMethod && !b.defaultPaymentMethod) return -1;
          if (!a.defaultPaymentMethod && b.defaultPaymentMethod) return 1;
          // If both or neither are default, sort by creation date
          return new Date(b.creationDate).getTime() - new Date(a.creationDate).getTime();
        });
      }

      set({ paymentMethods });
    } catch (error) {
      logger.error('Failed to fetch payment methods', error, {
        feature: 'paymentMethod-store',
        route: 'getAllPaymentMethods',
      });
      set({ error: 'Failed to fetch payment methods. Please try again.' });
    } finally {
      set({ isLoading: false });
    }
  },

  createPaymentMethod: async (paymentMethod: PaymentMethod, recaptchaToken?: string) => {
    try {
      set({ isLoading: true, error: null });
      const newPaymentMethod = await paymentMethodService.createPaymentMethod(
        paymentMethod,
        recaptchaToken
      );
      if (newPaymentMethod) {
        set({ isLoading: false });
        await get().getAllPaymentMethods();
      }
      return newPaymentMethod;
    } catch (error) {
      logger.error('Failed to create payment method', error, {
        feature: 'paymentMethod-store',
        route: 'createPaymentMethod',
      });
      set({
        error: extractApiErrorMessage(
          error,
          'Failed to add payment method. Please try again.'
        ),
      });
      return null;
    } finally {
      set({ isLoading: false });
    }
  },

  updatePaymentMethod: async (
    id: string,
    paymentMethod: PaymentMethod,
    recaptchaToken?: string
  ) => {
    try {
      set({ isLoading: true, error: null });
      const updatedPaymentMethod = await paymentMethodService.updatePaymentMethod(
        id,
        paymentMethod,
        recaptchaToken
      );
      if (updatedPaymentMethod) {
        set({ isLoading: false });
        await get().getAllPaymentMethods();
      }
      return updatedPaymentMethod;
    } catch (error) {
      logger.error('Failed to update payment method', error, {
        feature: 'paymentMethod-store',
        route: 'updatePaymentMethod',
        id,
      });
      set({
        error: extractApiErrorMessage(
          error,
          'Failed to update payment method. Please try again.'
        ),
      });
      return null;
    } finally {
      set({ isLoading: false });
    }
  },

  deletePaymentMethod: async (id: string, recaptchaToken?: string) => {
    try {
      set({ isLoading: true, error: null });
      await paymentMethodService.deletePaymentMethod(id, recaptchaToken);
      set({ isLoading: false });
      await get().getAllPaymentMethods();
    } catch (error) {
      logger.error('Failed to delete payment method', error, {
        feature: 'paymentMethod-store',
        route: 'deletePaymentMethod',
        id,
      });
      set({ error: 'Failed to delete payment method. Please try again.' });
      throw error; // Re-throw so UI can handle it
    } finally {
      set({ isLoading: false });
    }
  },

  createStripeAchSetupIntent: async (garageId: string) => {
    return paymentMethodService.createStripeAchSetupIntent(garageId);
  },

  getStripeSetupIntent: async (setupIntentId: string, garageId: string) => {
    return paymentMethodService.getStripeSetupIntent(setupIntentId, garageId);
  },

  refreshStripeAchStatus: async (paymentMethodId: string, garageId: string) => {
    return paymentMethodService.refreshStripeAchStatus(paymentMethodId, garageId);
  },

  refreshAdyenAchStatus: async (paymentMethodId: string, garageId: string) => {
    return paymentMethodService.refreshAdyenAchStatus(paymentMethodId, garageId);
  },

  createPaypalSetupToken: async (garageId: string) => {
    return paymentMethodService.createPaypalSetupToken(garageId);
  },

  setDefaultPaymentMethod: async (id: string, recaptchaToken?: string) => {
    try {
      set({ isLoading: true, error: null });
      const currentPaymentMethods = get().paymentMethods;
      await paymentMethodService.setDefaultPaymentMethod(id, currentPaymentMethods, recaptchaToken);
      set({ isLoading: false });
      await get().getAllPaymentMethods();
    } catch (error) {
      logger.error('Failed to set default payment method', error, {
        feature: 'paymentMethod-store',
        route: 'setDefaultPaymentMethod',
        id,
      });
      set({ error: 'Failed to set primary payment method. Please try again.' });
    } finally {
      set({ isLoading: false });
    }
  },
}));
